import { useState, type ReactNode } from 'react';

/**
 * Per-section collapse state, persisted to localStorage so an admin's
 * expand/collapse choices stick across visits. Defaults to expanded when there's
 * no stored value.
 */
function useSectionCollapsed(sectionId: string): [boolean, () => void] {
  const storageKey = `adminConfig:section:${sectionId}:collapsed`;
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(storageKey) === 'true';
    } catch {
      return false;
    }
  });
  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(storageKey, String(next));
      } catch {
        // ignore storage errors (private mode, quota, etc.)
      }
      return next;
    });
  };
  return [collapsed, toggle];
}

/**
 * A top-level AdminConfig section with a clickable header that expands/collapses
 * its body. Header matches the existing uppercase section-label styling; a
 * chevron points down when expanded, right when collapsed. State persists via
 * {@link useSectionCollapsed}.
 *
 * Lives in its own module (rather than inline in AdminConfigPage) so sections
 * large enough to warrant their own file can use it without importing back into
 * AdminConfigPage and creating a cycle.
 */
export function CollapsibleSection({
  sectionId,
  title,
  children,
}: {
  sectionId: string;
  title: string;
  children: ReactNode;
}) {
  const [collapsed, toggle] = useSectionCollapsed(sectionId);
  return (
    <section>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={!collapsed}
        className="w-full flex items-center justify-between gap-2 mb-3 text-left cursor-pointer group"
      >
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 group-hover:text-gray-700">
          {title}
        </h3>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${collapsed ? '-rotate-90' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {!collapsed && <div className="space-y-3">{children}</div>}
    </section>
  );
}
